import { type FC, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import { FormattedMessage } from 'dibs-react-intl';
import { GridRow, GridCol } from 'dibs-elements/exports/Grid.tsx';

import dibsCss from 'dibs-css';

import {
    useItemTranslations,
    INTERFACE_STATE,
} from '../PdpSharedOriginalLanguageProvider/PdpSharedOriginalLanguageProvider.tsx';
import { PdpSharedContactDealerButton } from '../PdpSharedButtons/PdpSharedContactDealerButton.tsx';
import { getAttributeInfo } from '../helpers/getAttributeInfo.ts';

import { type PdpRespTabsCustomizationPanel_user$key } from './__generated__/PdpRespTabsCustomizationPanel_user.graphql.ts';
import { type PdpRespTabsCustomizationPanel_item$key } from './__generated__/PdpRespTabsCustomizationPanel_item.graphql.ts';

type Props = {
    user: PdpRespTabsCustomizationPanel_user$key | null;
    item: PdpRespTabsCustomizationPanel_item$key;
    isAccordionLayout?: boolean;
};

const userFragment = graphql`
    fragment PdpRespTabsCustomizationPanel_user on User {
        ...PdpSharedContactDealerButton_user
    }
`;

const itemFragment = graphql`
    fragment PdpRespTabsCustomizationPanel_item on Item {
        isDyoCustomizable
        attributes {
            ...getAttributeInfo_itemAttributes
        }
        ...PdpSharedContactDealerButton_item
    }
`;

export const PdpRespTabsCustomizationPanel: FC<Props> = ({
    user: userRef,
    item: itemRef,
    isAccordionLayout = false,
}) => {
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);
    const [state] = useItemTranslations();
    const itemAttributes =
        state?.interfaceState === INTERFACE_STATE.SHOW_ORIGINAL
            ? state?.data?.viewer?.item?.attributes
            : item?.attributes;
    const { isDyoCustomizable, attributes } = item;
    const { customizationOptions = null, isResizable = null } = getAttributeInfo(
        itemAttributes || attributes
    );

    const resizableContent = isResizable ? (
        <FormattedMessage
            id="PdpRespTabsCustomizationPanel.resizingAvailable"
            defaultMessage="Resizing is available."
        />
    ) : null;

    const dyoCopy = isDyoCustomizable ? (
        <FormattedMessage
            id="PdpRespTabsCustomizationPanel.designYouOwn"
            defaultMessage="This gemstone can be customized into an extraordinary work of art under your direction. Collaborate with the jeweler to create a one-of-a-kind piece. Simply complete the form provided, and the seller will contact you."
        />
    ) : null;

    const launchOrigin = isDyoCustomizable
        ? 'dyo secondary cta'
        : 'contact link in pdp - description';

    const contactDealerButton: ReactNode = (
        <PdpSharedContactDealerButton
            size={isAccordionLayout ? 'medium' : 'small'}
            fullWidth={isAccordionLayout ? false : undefined}
            buttonType="transparent"
            buttonStyle={isAccordionLayout ? 'circle' : undefined}
            launchOrigin={launchOrigin}
            type={isDyoCustomizable ? 'REQUEST_CUSTOMIZATION_ALT' : 'REQUEST_CUSTOMIZATION'}
            item={item}
            user={user}
            wrapperClassName={classnames({ [dibsCss.mtSmall]: !isAccordionLayout }, dibsCss.wFit)}
        />
    );

    if (isAccordionLayout) {
        const hasTextContent = resizableContent || customizationOptions || dyoCopy;

        return (
            <div
                className={classnames(
                    dibsCss.flex,
                    dibsCss.flexCol,
                    dibsCss.gapSmall,
                    dibsCss.itemsStart,
                    dibsCss.wFull
                )}
                data-tn="pdp-customization-panel"
            >
                {hasTextContent && (
                    <div
                        className={classnames(
                            dibsCss.flex,
                            dibsCss.flexCol,
                            dibsCss.spaceYXxsmall,
                            dibsCss.sassyFontBodyMedium,
                            dibsCss.textNoir,
                            dibsCss.wFull,
                            dibsCss.minWFull,
                            dibsCss.breakWords,
                            dibsCss.whitespacePreLine
                        )}
                        data-tn="pdp-customization-panel-content"
                    >
                        {resizableContent && <div>{resizableContent}</div>}
                        {customizationOptions && <div>{customizationOptions}</div>}
                        {dyoCopy && <div>{dyoCopy}</div>}
                    </div>
                )}
                {contactDealerButton}
            </div>
        );
    }

    return (
        <GridRow>
            <GridCol desktop={4}>
                <div className={dibsCss.sassyFontBody}>
                    {resizableContent ? <div>{resizableContent}</div> : null}
                    {customizationOptions}
                    {dyoCopy}
                </div>
                {contactDealerButton}
            </GridCol>
        </GridRow>
    );
};
